import { Button, Checkbox, Css, ModalBody, ModalFooter, ModalHeader, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { useState } from "react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { DimensionTypeCheckboxes } from "src/routes/bids/plan-based-bid-package/components/DimensionTypeCheckboxes";
import { PersonalizationGroupsField } from "src/routes/bids/plan-based-bid-package/components/PersonalizationGroupsField";
import {
  buildDimensions,
  buildPersonalizationDimensions,
  nextGroupClientId,
  PlanBasedBidPackageBidPlanGroup,
  PlanBasedBidPackageFormState,
  renumberGroups,
} from "src/routes/bids/plan-based-bid-package/utils";

type BasePlanGroupDimensionsModalProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  // When set, the modal edits this base group's dimensions; otherwise it creates new base groups on save.
  editingGroup?: ObjectState<PlanBasedBidPackageBidPlanGroup>;
};

export function BasePlanGroupDimensionsModal(props: BasePlanGroupDimensionsModalProps) {
  const { formState, dimensionResults, editingGroup } = props;
  const { closeModal } = useModal();
  const isEdit = !!editingGroup;
  const existingDimensions = editingGroup?.dimensions.value ?? [];
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>(
    existingDimensions.map((d) => d.globalOptionTypeId).compact(),
  );
  // The group's "primary" personalization GlobalOptionGroup, which drives the group's name if set
  const personalizationGroupId = editingGroup?.primaryOptionGroupId.value;
  // Add mode only: the personalization options to split into one base group each (each one names its own group).
  const [primaryGroupIds, setPrimaryGroupIds] = useState<string[]>([]);
  // Add/edit modes: additional personalization groups stacked as shared co-dimensions.
  const [additionalGroupIds, setAdditionalGroupIds] = useState<string[]>(
    isEdit
      ? existingDimensions
          .map((d) => d.globalOptionGroupId)
          .compact()
          .filter((id) => id !== personalizationGroupId)
      : [],
  );
  const [showAdditionalGroup, setShowAdditionalGroup] = useState<boolean>(additionalGroupIds.nonEmpty);
  // Don't offer a group that's already the primary (edit) or one being split into its own group (add).
  const excludePrimaryGroupIds = isEdit ? [personalizationGroupId].compact() : primaryGroupIds;

  function save() {
    const additionalIds = showAdditionalGroup ? additionalGroupIds : [];
    if (isEdit && editingGroup) {
      const personalizationIds = [personalizationGroupId, ...additionalIds].compact();
      // If the group has no primary yet, the first personalization added takes ownership and names it.
      if (!personalizationGroupId && personalizationIds.nonEmpty) {
        editingGroup.primaryOptionGroupId.set(personalizationIds.first);
      }
      editingGroup.dimensions.set([
        ...buildDimensions(selectedTypeIds, existingDimensions, dimensionResults),
        ...buildPersonalizationDimensions(personalizationIds, existingDimensions, dimensionResults),
      ]);
    } else {
      // Each selected personalization zone becomes its own base group; the type price dimensions and any
      // stacked additional personalization zones are shared across them.
      const groups = formState.bidPlanGroups.value ?? [];
      const typeDims = buildDimensions(selectedTypeIds, [], dimensionResults);
      const newGroups: PlanBasedBidPackageBidPlanGroup[] = [];
      for (const primaryId of primaryGroupIds) {
        // The primary plus any shared additional groups all become dimensions
        const personalizationIds = [primaryId, ...additionalIds].compact();
        newGroups.push({
          clientId: nextGroupClientId([...groups, ...newGroups], "base"),
          archive: false,
          primaryOptionGroupId: primaryId,
          dimensions: [...typeDims, ...buildPersonalizationDimensions(personalizationIds, [], dimensionResults)],
        });
      }
      formState.bidPlanGroups.set(renumberGroups([...groups, ...newGroups]));
    }
    closeModal();
  }

  return (
    <>
      <ModalHeader>{isEdit ? "Edit Base Bid Personalization Zone" : "Add Base Bid Personalization Zone"}</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap3.$}>
          <div>
            Define zones within the plan that have configurable options. Cost lines populate only for product offerings
            that include the corresponding zone.
          </div>
          {(!isEdit || !!personalizationGroupId) && (
            <PersonalizationGroupsField
              label="Personalization Zone"
              readOnly={isEdit}
              dimensionResults={dimensionResults}
              selectedGroupIds={isEdit ? [personalizationGroupId].compact() : primaryGroupIds}
              onChange={setPrimaryGroupIds}
            />
          )}
          <div css={Css.df.fdc.gap3.$}>
            <div>Select cost dimensions for these personalization zones.</div>
            <DimensionTypeCheckboxes
              dimensionResults={dimensionResults}
              selectedTypeIds={selectedTypeIds}
              onChange={setSelectedTypeIds}
            />
            {/* Optionally stack one or more additional personalization zones as shared co-dimensions. */}
            <>
              <Checkbox
                label="Personalization Zone"
                selected={showAdditionalGroup}
                onChange={(next) => {
                  setShowAdditionalGroup(next);
                  if (!next) setAdditionalGroupIds([]);
                }}
              />
              {showAdditionalGroup && (
                <PersonalizationGroupsField
                  label="Select a Personalization Zone"
                  excludeGroupIds={excludePrimaryGroupIds}
                  dimensionResults={dimensionResults}
                  selectedGroupIds={additionalGroupIds}
                  onChange={setAdditionalGroupIds}
                />
              )}
            </>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="tertiary" onClick={closeModal} />
        <Button label="Save" onClick={save} disabled={!isEdit && primaryGroupIds.isEmpty} />
      </ModalFooter>
    </>
  );
}
